/* eslint-disable @typescript-eslint/no-unused-vars */
// Styles
import '@common/styles/pages/case-studies.css'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

// Packages
import React, { useEffect, useRef } from 'react'
import { graphql, PageProps } from 'gatsby'

// Components
import NewFooter from '@modules/common/components/NewFooter'
import Meta from '@components/Meta'
import TestimonialSlider from '@modules/new-home/TestimonialSlider'
import AniLink from 'gatsby-plugin-transition-link/AniLink'
import ProcessSteps from '@modules/common/components/ProcessSteps'
import CaseStudy from '@modules/common/components/CaseStudy'

// Common
import { TypeCaseStudy } from '@common/types/CaseStudy'
import { TypeTestimonial } from '@common/types/Testimonial'
import MotionAniLink from '@modules/common/components/MotionAniLink'
import { TypeSEO } from '@common/types/SEO'
import Asterisk from '../../static/svgs/fullasterisk.inline.svg'

interface Props {
  data: {
    contentfulCaseStudies: {
      title: {
        title: string
      }
      subtitle: {
        subtitle: string
      }
      caseStudies: TypeCaseStudy[]
      testimonials: TypeTestimonial[]
      seo: TypeSEO
    }
  }
  location: PageProps['location']
}

const services = [
  {
    title: 'Investigate',
    description:
      'We don’t immediately jump into design and try to dazzle you with cool concepts. We start by understanding your goals, your market, your users and what’s next for your business.',
    icon: `<svg
                  fill="none"
                  height="73"
                  viewBox="0 0 94 73"
                  width="94"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_2363_1595)">
                    <path
                      d="M23.2656 18.9514C22.3776 18.8311 21.4694 18.751 20.541 18.751C9.48112 18.751 0.5 27.6683 0.5 38.6497C0.5 49.631 9.48112 58.5484 20.541 58.5484C22.4583 58.5484 24.2949 58.2678 26.0508 57.7668C21.6914 51.6149 19.1283 44.1403 19.1283 36.0446C19.1283 29.8926 20.6419 24.0813 23.2656 18.9514Z"
                      fill="#020000"
                    />
                    <path
                      d="M57.2324 0.0546875C43.3268 0.0546875 31.2376 7.86988 25.1628 19.3121C22.4987 24.3219 20.9648 30.0129 20.9648 36.0647C20.9648 43.94 23.5684 51.2142 27.9076 57.1657C34.5072 66.1833 45.1634 72.0747 57.2324 72.0747C77.2331 72.0747 93.5 55.9233 93.5 36.0647C93.5 16.2061 77.2331 0.0546875 57.2324 0.0546875ZM57.2324 71.0527C56.6673 71.0527 56.1224 70.9926 55.5573 70.9726C62.6009 61.1134 66.7786 49.07 66.7786 36.0647C66.7786 23.0594 62.6211 10.996 55.5573 1.13679C56.1224 1.11675 56.6673 1.05664 57.2324 1.05664C76.668 1.05664 92.4909 16.7471 92.4909 36.0647C92.4909 55.3823 76.6881 71.0728 57.2324 71.0728V71.0527Z"
                      fill="#020000"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_2363_1595">
                      <rect
                        fill="white"
                        height="72"
                        transform="translate(0.5 0.0546875)"
                        width="93"
                      />
                    </clipPath>
                  </defs>
                </svg>`
  },
  {
    title: 'Ideate',
    description:
      'Every design decision starts with the user in mind. Whether you need design for your new app or need strategic direction, our talented team of UX/UI product designers will help you level up.',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="82" height="73" viewBox="0 0 82 73" fill="none">
            <g clip-path="url(#clip0_2363_1604)">
              <path d="M11.6724 36.0545C11.6724 32.9698 9.17079 30.4683 6.08615 30.4683C3.00151 30.4683 0.499945 32.9698 0.499944 36.0545C0.499944 39.1391 3.00151 41.6407 6.08615 41.6407C9.17079 41.6407 11.6724 39.1391 11.6724 36.0545ZM1.42158 36.0545C1.42158 33.4965 3.50935 31.4087 6.06735 31.4087C8.62534 31.4087 10.7131 33.4965 10.7131 36.0545C10.7131 38.6125 8.62534 40.7002 6.06735 40.7002C3.50935 40.7002 1.42158 38.6125 1.42158 36.0545Z" fill="#020000"/>
              <path d="M54.6127 72.0547C60.0108 72.0547 64.6189 64.9262 67.0641 54.1487C75.1519 52.0986 81.1519 44.782 81.1519 36.0547C81.1519 27.3274 75.1519 20.0296 67.0641 17.9606C64.6189 7.18321 60.0108 0.0546866 54.6127 0.0546863C52.3744 0.0546862 50.2867 1.31487 48.4246 3.5155C48.1989 3.47788 47.9732 3.44027 47.7475 3.44027C45.6221 3.44027 43.6283 4.68165 41.8603 6.88227C41.691 6.88227 41.5218 6.82585 41.3525 6.82585C39.3211 6.82585 37.4215 8.06722 35.7663 10.249C35.6158 10.249 35.4653 10.1926 35.2961 10.1926C33.3964 10.1926 31.6283 11.434 30.1048 13.5782C30.0672 13.5782 30.0484 13.5782 30.0108 13.5782C28.224 13.5782 26.5688 14.8196 25.1769 16.9638C25.1205 16.9638 25.0829 16.9638 25.0453 16.9638C20.6816 16.9638 17.2772 25.3713 17.2772 36.1111C17.2772 46.8509 20.6816 55.2584 25.0453 55.2584C25.1017 55.2584 25.1393 55.2584 25.1957 55.2584C26.5876 57.4026 28.2428 58.644 30.0296 58.644C30.0672 58.644 30.086 58.644 30.1236 58.644C31.6472 60.7882 33.4152 62.0296 35.3149 62.0296C35.4653 62.0296 35.6158 61.992 35.7663 61.9732C37.4214 64.155 39.3211 65.3964 41.3525 65.3964C41.5218 65.3964 41.691 65.3588 41.8603 65.3399C43.6283 67.5218 45.6033 68.782 47.7475 68.782C47.9732 68.782 48.1989 68.7255 48.4246 68.7067C50.2867 70.9073 52.3932 72.1675 54.6127 72.1675L54.6127 72.0547ZM29.6158 36.0547C29.6158 39.9293 29.9167 43.597 30.4434 46.9638C29.8227 49.108 29.0515 50.8948 28.1487 52.1738C26.7193 47.7914 25.8352 42.1487 25.8352 36.0547C25.8352 29.9606 26.7193 24.3368 28.1487 19.9356C29.0515 21.1958 29.8227 23.0014 30.4434 25.1456C29.9167 28.4936 29.6158 32.1613 29.6158 36.0547ZM31.0453 27.6284C31.5531 30.1675 31.8541 33.0265 31.8541 36.0547C31.8541 39.0829 31.5531 41.9418 31.0453 44.481C30.7255 41.829 30.5562 38.9889 30.5562 36.0547C30.5562 33.1205 30.7255 30.2992 31.0453 27.6284ZM32.7945 36.0547C32.7945 31.9168 32.2867 28.1362 31.4214 25.0327C31.9293 21.9105 32.644 19.108 33.5092 16.6628C34.4497 17.9042 35.3149 19.597 36.0108 21.666C35.1832 25.992 34.713 30.8447 34.713 36.0359C34.713 41.2271 35.1832 46.0986 36.0108 50.4058C35.2961 52.4747 34.4497 54.1675 33.5092 55.4089C32.644 52.9826 31.9293 50.1613 31.4214 47.039C32.2866 43.9356 32.7945 40.155 32.7945 36.0171L32.7945 36.0547ZM36.6127 23.6409C37.5719 27.1769 38.155 31.4653 38.155 36.0547C38.155 40.644 37.5719 44.9324 36.6127 48.4685C35.992 44.6503 35.6534 40.4559 35.6534 36.0547C35.6534 31.6534 35.992 27.4591 36.6127 23.6409ZM39.0954 36.0547C39.0954 30.4685 38.3055 25.4277 37.0077 21.5343C37.5907 18.5625 38.3619 15.8352 39.2647 13.503C40.2804 14.782 41.2208 16.5312 42.0108 18.6566C40.9011 23.7914 40.2804 29.6973 40.2804 36.0547C40.2804 42.4121 40.9011 48.318 42.0108 53.4528C41.2208 55.5782 40.2804 57.3086 39.2647 58.6064C38.3619 56.2741 37.6095 53.5657 37.0077 50.5751C38.3243 46.6816 39.0954 41.6409 39.0954 36.0547ZM43.7976 36.0547C43.7976 37.8603 44.0609 39.5907 44.55 41.2459C44.2302 45.0829 43.5531 48.6378 42.6315 51.6284C41.7287 46.9262 41.2208 41.6597 41.2208 36.0547C41.2208 30.4497 41.7287 25.1832 42.6315 20.481C43.5531 23.4716 44.2302 27.0077 44.55 30.8635C44.0798 32.5186 43.7976 34.249 43.7976 36.0547ZM45.2835 28.7381C44.8509 24.8823 44.0609 21.4403 43.0077 18.5813C43.7036 15.4026 44.6064 12.5625 45.6221 10.1174C47.7098 12.5061 49.4403 16.5688 50.5876 21.6472C48.2929 23.5468 46.4497 25.9732 45.2647 28.7381L45.2835 28.7381ZM45.2835 43.3901C46.4685 46.1738 48.2929 48.6001 50.6064 50.481C49.4591 55.5594 47.7287 59.6409 45.6409 62.0108C44.6064 59.5657 43.7224 56.7067 43.0265 53.5469C44.0609 50.6879 44.8509 47.2459 45.3023 43.3901L45.2835 43.3901ZM51.434 21.0265C50.1926 15.76 48.3117 11.6033 46.0359 9.17694C46.8823 7.33368 47.8227 5.79136 48.8196 4.51237C52.4873 5.35876 55.6848 10.5124 57.6785 18.0359C55.4026 18.6378 53.2961 19.6722 51.434 21.0265ZM42.4058 17.0202C41.6158 15.1769 40.6942 13.6534 39.6785 12.5249C40.4497 10.7005 41.3149 9.12052 42.2553 7.86033C43.1769 8.06722 44.0798 8.59387 44.9262 9.38384C43.9481 11.6221 43.1017 14.1801 42.4058 17.0202ZM36.387 19.8791C35.6722 18.1487 34.8258 16.7193 33.9042 15.6472C34.5813 13.9356 35.3525 12.4309 36.1801 11.2271C37.0077 11.434 37.7976 11.9606 38.5688 12.713C37.7224 14.8196 36.9888 17.2271 36.387 19.8603L36.387 19.8791ZM30.8008 23.1519C30.1613 21.4026 29.3901 19.9544 28.5061 18.9011C29.1268 17.2083 29.8227 15.76 30.5939 14.5563C31.365 14.7067 32.0986 15.1581 32.8133 15.8729C32.0045 18.0171 31.3274 20.4622 30.8196 23.1519L30.8008 23.1519ZM24.8948 36.0547C24.8948 42.6189 25.8352 48.5437 27.3964 53.0578C26.8509 53.6221 26.2678 54.0171 25.666 54.1675C23.4089 50.2929 21.8666 43.5406 21.8666 36.0547C21.8666 28.5688 23.4089 21.8164 25.666 17.9418C26.2678 18.0923 26.8509 18.4873 27.3964 19.0516C25.8352 23.5657 24.8948 29.4904 24.8948 36.0547ZM30.8008 48.9763C31.3274 51.666 31.9857 54.1111 32.7945 56.2553C32.0986 56.9512 31.3462 57.4026 30.5751 57.5719C29.8039 56.387 29.108 54.9199 28.4873 53.2271C29.3525 52.1738 30.1236 50.7255 30.7819 48.9763L30.8008 48.9763ZM36.387 52.249C36.97 54.8823 37.7036 57.3086 38.5688 59.3964C37.8164 60.1487 37.0077 60.6754 36.1801 60.8823C35.3525 59.6785 34.5813 58.1738 33.9042 56.4622C34.8258 55.3901 35.6722 53.9794 36.387 52.2302L36.387 52.249ZM42.4058 55.108C43.1017 57.9481 43.9481 60.5249 44.9261 62.7443C44.0798 63.5343 43.1957 64.061 42.2553 64.2679C41.3149 63.0077 40.4497 61.4465 39.6785 59.6033C40.6942 58.4559 41.597 56.9512 42.4058 55.108ZM51.434 51.1017C53.2772 52.4559 55.3838 53.4904 57.6785 54.0923C55.6848 61.6158 52.4873 66.7506 48.8196 67.6158C47.8227 66.3556 46.8823 64.7945 46.0359 62.9512C48.3117 60.5249 50.1926 56.3682 51.434 51.1017ZM54.6127 0.995125C59.4277 0.995125 63.6597 7.70986 66.0108 17.7161C64.8635 17.4904 63.6785 17.3776 62.4559 17.3776C61.1205 17.3776 59.8227 17.528 58.5813 17.7914C56.55 10.1738 53.2772 4.94497 49.4214 3.7224C51.0202 1.97318 52.7694 0.995125 54.5939 0.995125L54.6127 0.995125ZM47.691 4.3619C46.8258 5.52804 46.0359 6.91989 45.2835 8.48102C44.5124 7.8039 43.7036 7.31487 42.8572 7.05155C44.3431 5.33995 45.9606 4.3807 47.691 4.3619ZM41.1644 7.74748C40.3556 8.89481 39.6221 10.249 38.945 11.7726C38.249 11.1331 37.5155 10.6816 36.7631 10.4183C38.0986 8.76315 39.6033 7.78509 41.1644 7.72867L41.1644 7.74748ZM35.1268 11.1142C34.412 12.2052 33.7537 13.4653 33.1519 14.9136C32.5124 14.3494 31.854 13.9356 31.1393 13.7287C32.3431 12.1299 33.6973 11.1895 35.108 11.1331L35.1268 11.1142ZM29.4089 14.5374C28.7882 15.5531 28.2428 16.7569 27.7349 18.0735C27.2271 17.6221 26.7004 17.2835 26.155 17.0954C27.1519 15.6472 28.2616 14.7631 29.4277 14.5186L29.4089 14.5374ZM18.1801 36.0359C18.1801 26.5751 21.0578 18.5249 24.5562 17.8854C22.318 21.9481 20.9073 28.4936 20.9073 36.0359C20.9073 43.5782 22.3368 50.1237 24.5562 54.1863C21.0578 53.5468 18.1801 45.5155 18.1801 36.0359ZM26.1362 54.9763C26.6816 54.7882 27.2271 54.4497 27.7161 53.9983C28.224 55.3149 28.7882 56.5186 29.3901 57.5343C28.224 57.3086 27.1142 56.4058 26.1362 54.9763ZM31.1393 58.3619C31.8352 58.155 32.5124 57.7412 33.1519 57.1769C33.7537 58.6064 34.412 59.8854 35.1268 60.9763C33.7161 60.9199 32.3619 59.9795 31.1581 58.3807L31.1393 58.3619ZM36.7443 61.6534C37.4967 61.3901 38.2302 60.9387 38.9261 60.2992C39.6033 61.8415 40.3368 63.1958 41.1456 64.3431C39.5656 64.2867 38.0798 63.3274 36.7443 61.6534ZM42.8384 65.039C43.6848 64.7757 44.4935 64.2867 45.2647 63.6095C45.9982 65.1707 46.807 66.5625 47.6722 67.7287C45.9606 67.7287 44.3243 66.7506 42.8384 65.039ZM49.4026 68.387C53.2584 67.1832 56.5312 61.9356 58.5625 54.318C59.8227 54.5813 61.1017 54.7318 62.4371 54.7318C63.6597 54.7318 64.8446 54.6001 65.992 54.3932C63.6221 64.4183 59.4089 71.1142 54.5939 71.1142C52.7506 71.1142 51.0014 70.1362 49.4214 68.387L49.4026 68.387Z" fill="#020000"/>
            </g>
            <defs>
              <clipPath id="clip0_2363_1604">
                <rect width="72" height="80.652" fill="white" transform="translate(81.1519 0.0546875) rotate(90)"/>
              </clipPath>
            </defs>
          </svg>`
  },
  {
    title: 'Iterate',
    description:
      'No design ends with a deliverable. We constantly learn and optimize our designs, leveraging analytics to surface user behavior while running A/B tests to drive performance.',
    icon: `<svg xmlns="http://www.w3.org/2000/svg" width="137" height="73" viewBox="0 0 137 73" fill="none">
  <g clip-path="url(#clip0_2363_1613)">
    <path d="M1.53079 22.3458C1.86516 23.2374 2.28312 24.1012 2.7568 24.9371L3.95494 24.2405C3.50912 23.4882 3.11903 22.6801 2.81253 21.8721L1.50293 22.3458H1.53079Z" fill="#020000"/>
    <path d="M2.08824 14.0701L0.72291 13.8472C0.583591 14.7388 0.5 15.6583 0.5 16.5778V16.7171L1.89319 16.5778C1.89319 15.7419 1.94892 14.906 2.08824 14.0979V14.0701Z" fill="#020000"/>
    <path d="M31.3454 24.7698L30.1472 24.0732C29.7014 24.8256 29.1999 25.55 28.6426 26.2466L29.7014 27.1383C30.3144 26.4138 30.8717 25.6057 31.3454 24.7698Z" fill="#020000"/>
    <path d="M2.61768 8.44166L3.84368 9.13826C4.26164 8.38594 4.76319 7.63361 5.32046 6.96488L4.23378 6.07324C3.62077 6.82556 3.09136 7.60575 2.61768 8.44166Z" fill="#020000"/>
    <path d="M25.3546 30.8441L24.658 29.646C23.9056 30.0918 23.0976 30.454 22.2896 30.7605L22.7632 32.0701C23.6549 31.7358 24.5465 31.3178 25.3546 30.8441Z" fill="#020000"/>
    <path d="M31.9302 19.1412L33.2955 19.3641C33.4627 18.4446 33.5463 17.4972 33.5463 16.5499V16.4941L32.1531 16.5499C32.1531 17.4136 32.0695 18.2774 31.9302 19.1412Z" fill="#020000"/>
    <path d="M27.6114 3.89982C26.8869 3.28682 26.0789 2.72954 25.243 2.25586L24.5464 3.454C25.2987 3.89982 26.0232 4.40137 26.6919 4.95865L27.5835 3.89982H27.6114Z" fill="#020000"/>
    <path d="M6.49072 29.3118C7.21518 29.9248 8.02323 30.482 8.85914 30.9557L9.55574 29.7576C8.80342 29.3118 8.05109 28.8102 7.38236 28.2529L6.49072 29.3118Z" fill="#020000"/>
    <path d="M32.5155 10.8936C32.1811 10.0019 31.7631 9.1103 31.2895 8.30225L30.0913 8.99884C30.5371 9.75116 30.8994 10.5592 31.2059 11.3673L32.5155 10.8936Z" fill="#020000"/>
    <path d="M19.6424 1.67079L19.8931 0.305461C18.9458 0.138279 17.9984 0.0546875 17.051 0.0546875H16.8281L17.0232 1.44788C17.8869 1.44788 18.7786 1.53147 19.6424 1.67079Z" fill="#020000"/>
    <path d="M8.60843 2.36715L9.30502 3.56529C10.0573 3.11947 10.8654 2.72938 11.6734 2.42288L11.1719 1.11328C10.2803 1.44765 9.41648 1.8656 8.58057 2.36715H8.60843Z" fill="#020000"/>
    <path d="M14.4876 31.4849L14.2646 32.8502C15.1563 32.9895 16.1037 33.0731 17.051 33.0731V32.683L17.0232 31.6799C16.1872 31.6799 15.3235 31.5964 14.4876 31.457V31.4849Z" fill="#020000"/>
    <path d="M80.0595 71.1372C88.6874 69.7367 94.5464 61.6071 93.1459 52.9792C91.7454 44.3513 83.6158 38.4924 74.9879 39.8929C66.36 41.2934 60.5011 49.423 61.9016 58.0509C63.3021 66.6787 71.4317 72.5377 80.0595 71.1372Z" fill="#020000"/>
    <path d="M119.479 39.0083C122.627 39.0083 125.497 40.4572 127.754 42.8814C131.878 45.5842 134.609 50.2374 134.609 55.5315C134.609 60.8256 131.878 65.4789 127.754 68.1817C125.525 70.6058 122.627 72.0547 119.479 72.0547C128.59 72.0547 136.002 64.643 136.002 55.5315C136.002 46.4201 128.59 39.0083 119.479 39.0083Z" fill="#020000"/>
    <path d="M111.203 68.1817C107.079 65.4789 104.348 60.8256 104.348 55.5315C104.348 50.2374 107.079 45.5842 111.203 42.8814C113.432 40.4572 116.33 39.0083 119.478 39.0083C110.367 39.0083 102.955 46.4201 102.955 55.5315C102.955 64.643 110.367 72.0547 119.478 72.0547C116.33 72.0547 113.46 70.6058 111.203 68.1817Z" fill="#020000"/>
    <path d="M114.937 69.4074C110.869 67.0947 107.999 61.7449 107.999 55.5313C107.999 49.3176 110.869 43.9678 114.937 41.6551C115.132 41.4043 115.327 41.1814 115.522 40.9585C113.962 41.3765 112.513 42.0452 111.203 42.8811C108.389 45.9182 106.578 50.46 106.578 55.5313C106.578 60.6025 108.361 65.1443 111.203 68.1814C112.513 69.0452 113.962 69.6861 115.522 70.104C115.327 69.8811 115.132 69.6582 114.937 69.4074Z" fill="#020000"/>
    <path d="M124.02 41.6551C128.088 43.9678 130.958 49.3176 130.958 55.5313C130.958 61.7449 128.088 67.0947 124.02 69.4074C123.825 69.6582 123.63 69.8811 123.435 70.104C124.995 69.6861 126.444 69.0173 127.754 68.1814C130.568 65.1443 132.379 60.6025 132.379 55.5313C132.379 50.46 130.596 45.9182 127.754 42.8811C126.444 42.0173 124.995 41.3765 123.435 40.9585C123.63 41.1814 123.825 41.4043 124.02 41.6551Z" fill="#020000"/>
    <path d="M127.754 68.1816C126.444 69.0454 124.996 69.6863 123.435 70.1042C122.265 71.3302 120.927 72.0547 119.479 72.0547C122.627 72.0547 125.497 70.6058 127.754 68.1816Z" fill="#020000"/>
    <path d="M111.203 42.8814C112.512 42.0176 113.961 41.3767 115.522 40.9588C116.692 39.7328 118.029 39.0083 119.478 39.0083C116.33 39.0083 113.46 40.4572 111.203 42.8814Z" fill="#020000"/>
    <path d="M127.754 42.8814C125.525 40.4572 122.627 39.0083 119.479 39.0083C120.927 39.0083 122.265 39.7049 123.435 40.9588C124.996 41.3767 126.444 42.0455 127.754 42.8814Z" fill="#020000"/>
    <path d="M111.203 68.1816C113.432 70.6058 116.33 72.0547 119.478 72.0547C118.029 72.0547 116.692 71.3581 115.522 70.1042C113.961 69.6863 112.512 69.0176 111.203 68.1816Z" fill="#020000"/>
    <path d="M121.178 40.9032C124.131 42.6029 126.416 48.5936 126.416 55.5317C126.416 62.4698 124.159 68.4605 121.178 70.1602C121.122 70.2716 121.094 70.3831 121.039 70.4945C122.097 70.2995 123.1 69.9373 124.048 69.4079C126.333 66.4822 127.809 61.411 127.809 55.5317C127.809 49.6524 126.333 44.5812 124.048 41.6555C123.1 41.1261 122.097 40.7639 121.039 40.5688C121.094 40.6803 121.122 40.7918 121.178 40.9032Z" fill="#020000"/>
    <path d="M114.936 41.6555C112.652 44.5812 111.175 49.6524 111.175 55.5317C111.175 61.411 112.652 66.4822 114.936 69.4079C115.884 69.9373 116.887 70.2995 117.946 70.4945C117.89 70.3831 117.862 70.2716 117.806 70.1602C114.853 68.4605 112.568 62.4698 112.568 55.5317C112.568 48.5936 114.825 42.6029 117.806 40.9032C117.862 40.7918 117.89 40.6803 117.946 40.5688C116.887 40.7639 115.884 41.1261 114.936 41.6555Z" fill="#020000"/>
    <path d="M114.937 69.4077C115.132 69.6585 115.327 69.8814 115.522 70.1043C116.302 70.3272 117.11 70.4944 117.946 70.578C117.946 70.578 117.946 70.5223 117.918 70.4944C116.859 70.2994 115.856 69.9371 114.909 69.4077H114.937Z" fill="#020000"/>
    <path d="M114.937 41.6551C115.884 41.1257 116.887 40.7635 117.946 40.5685C117.946 40.5685 117.946 40.5127 117.974 40.4849C117.138 40.5685 116.33 40.7356 115.55 40.9585C115.354 41.1815 115.159 41.4044 114.964 41.6551H114.937Z" fill="#020000"/>
    <path d="M124.02 69.4077C123.073 69.9371 122.07 70.2994 121.011 70.4944C121.011 70.4944 121.011 70.5501 120.983 70.578C121.819 70.4944 122.627 70.3272 123.407 70.1043C123.602 69.8814 123.797 69.6585 123.992 69.4077H124.02Z" fill="#020000"/>
    <path d="M124.02 41.6551C123.825 41.4044 123.63 41.1815 123.435 40.9585C122.655 40.7356 121.847 40.5685 121.011 40.4849C121.011 40.4849 121.011 40.5406 121.039 40.5685C122.097 40.7635 123.101 41.1257 124.048 41.6551H124.02Z" fill="#020000"/>
    <path d="M121.011 70.5777C120.593 71.4972 120.092 72.0545 119.479 72.0545C120.927 72.0545 122.265 71.3579 123.435 70.104C122.655 70.3269 121.847 70.4941 121.011 70.5777Z" fill="#020000"/>
    <path d="M121.011 40.4851C121.847 40.5687 122.655 40.7359 123.435 40.9588C122.265 39.7328 120.927 39.0083 119.479 39.0083C120.092 39.0083 120.593 39.5656 121.011 40.4851Z" fill="#020000"/>
    <path d="M117.946 40.4851C118.364 39.5656 118.865 39.0083 119.478 39.0083C118.029 39.0083 116.692 39.7049 115.521 40.9588C116.302 40.7359 117.11 40.5687 117.946 40.4851Z" fill="#020000"/>
    <path d="M117.946 70.5777C117.11 70.4941 116.302 70.3269 115.521 70.104C116.692 71.33 118.029 72.0545 119.478 72.0545C118.865 72.0545 118.364 71.4972 117.946 70.5777Z" fill="#020000"/>
    <path d="M122.766 55.5314C122.766 54.2218 122.711 44.9432 121.178 40.9029C120.649 40.5964 120.063 40.4014 119.478 40.4014C118.893 40.4014 118.336 40.5964 117.806 40.9029C116.274 44.9432 116.218 54.2218 116.218 55.5314C116.218 56.841 116.274 66.1196 117.806 70.1599C118.336 70.4664 118.921 70.6614 119.478 70.6614C120.036 70.6614 120.621 70.4664 121.178 70.1599C122.711 66.1196 122.766 56.841 122.766 55.5314ZM117.584 55.5314C117.584 46.3085 118.782 41.3209 119.478 40.485C120.175 41.3209 121.373 46.3085 121.373 55.5314C121.373 64.7543 120.175 69.7419 119.478 70.5778C118.782 69.7419 117.584 64.7543 117.584 55.5314Z" fill="#020000"/>
    <path d="M121.039 40.5686C120.537 40.485 120.008 40.4014 119.479 40.4014C120.064 40.4014 120.621 40.5964 121.178 40.9029C121.122 40.7915 121.095 40.68 121.039 40.5686Z" fill="#020000"/>
    <path d="M117.918 70.4945C118.42 70.5781 118.949 70.6617 119.478 70.6617C118.893 70.6617 118.336 70.4667 117.807 70.1602C117.862 70.2716 117.89 70.3831 117.946 70.4945H117.918Z" fill="#020000"/>
    <path d="M117.807 40.9029C118.336 40.5964 118.921 40.4014 119.479 40.4014C118.949 40.4014 118.42 40.4571 117.918 40.5686C117.862 40.68 117.835 40.7915 117.779 40.9029H117.807Z" fill="#020000"/>
    <path d="M121.178 70.1602C120.649 70.4667 120.064 70.6617 119.479 70.6617C120.008 70.6617 120.537 70.606 121.039 70.4945C121.095 70.3831 121.122 70.2716 121.178 70.1602Z" fill="#020000"/>
    <path d="M119.479 70.6613C119.98 70.6613 120.509 70.6613 121.011 70.5777C121.011 70.5777 121.011 70.522 121.039 70.4941C120.537 70.5777 120.008 70.6613 119.479 70.6613Z" fill="#020000"/>
    <path d="M117.946 70.5777C118.447 70.6335 118.949 70.6613 119.478 70.6613C118.949 70.6613 118.42 70.6056 117.918 70.4941C117.918 70.4941 117.918 70.5499 117.946 70.5777Z" fill="#020000"/>
    <path d="M119.478 40.4014C118.977 40.4014 118.447 40.4014 117.946 40.485C117.946 40.485 117.946 40.5407 117.918 40.5686C118.42 40.485 118.949 40.4014 119.478 40.4014Z" fill="#020000"/>
    <path d="M121.011 40.485C120.509 40.4292 120.008 40.4014 119.479 40.4014C120.008 40.4014 120.537 40.4571 121.039 40.5686C121.039 40.5686 121.039 40.5128 121.011 40.485Z" fill="#020000"/>
    <path d="M121.011 70.5781C120.509 70.6339 120.008 70.6617 119.478 70.6617C118.949 70.6617 118.447 70.6617 117.946 70.5781C118.364 71.4976 118.865 72.0549 119.478 72.0549C120.091 72.0549 120.593 71.4976 121.011 70.5781Z" fill="#020000"/>
    <path d="M117.946 40.4851C118.447 40.4294 118.949 40.4015 119.478 40.4015C120.008 40.4015 120.509 40.4015 121.011 40.4851C120.593 39.5656 120.091 39.0083 119.478 39.0083C118.865 39.0083 118.364 39.5656 117.946 40.4851Z" fill="#020000"/>
    <path d="M35.5247 39.0083C26.4132 39.0083 19.0015 46.4201 19.0015 55.5315C19.0015 64.643 26.4132 72.0547 35.5247 72.0547C44.6361 72.0547 52.0479 64.643 52.0479 55.5315C52.0479 46.4201 44.6361 39.0083 35.5247 39.0083ZM35.5247 70.6337C27.1934 70.6337 20.3947 63.8628 20.3947 55.5037C20.3947 47.1445 27.1656 40.3736 35.5247 40.3736C43.8838 40.3736 50.6547 47.1445 50.6547 55.5037C50.6547 63.8628 43.8838 70.6337 35.5247 70.6337Z" fill="#020000"/>
    <path d="M35.5249 43.5498C28.9212 43.5498 23.5435 48.9275 23.5435 55.5312C23.5435 62.1349 28.9212 67.5127 35.5249 67.5127C42.1286 67.5127 47.5063 62.1349 47.5063 55.5312C47.5063 48.9275 42.1286 43.5498 35.5249 43.5498ZM35.5249 66.1473C29.6735 66.1473 24.9366 61.3826 24.9366 55.5591C24.9366 49.7356 29.7014 44.9709 35.5249 44.9709C41.3484 44.9709 46.1131 49.7356 46.1131 55.5591C46.1131 61.3826 41.3484 66.1473 35.5249 66.1473Z" fill="#020000"/>
    <path d="M35.525 49.0391C31.9584 49.0391 29.0327 51.9369 29.0327 55.5313C29.0327 59.1257 31.9305 62.0236 35.525 62.0236C39.1194 62.0236 42.0172 59.1257 42.0172 55.5313C42.0172 51.9369 39.1194 49.0391 35.525 49.0391ZM35.525 60.6304C32.7107 60.6304 30.4259 58.3456 30.4259 55.5313C30.4259 52.7171 32.7107 50.4323 35.525 50.4323C38.3392 50.4323 40.624 52.7171 40.624 55.5313C40.624 58.3456 38.3392 60.6304 35.525 60.6304Z" fill="#020000"/>
    <path d="M106.243 21.5932C111.454 20.618 114.769 18.5839 114.769 16.6892C114.769 14.7944 111.454 12.7604 106.243 11.7852C106.438 13.3177 106.55 14.9616 106.55 16.6892C106.55 18.4167 106.438 20.0607 106.243 21.5932Z" fill="#020000"/>
    <path d="M104.766 11.5624C103.178 11.3395 101.479 11.2002 99.6396 11.2002C97.8005 11.2002 96.1009 11.3395 94.5126 11.5624C94.2897 13.1507 94.1504 14.8503 94.1504 16.6894C94.1504 18.5284 94.2897 20.2281 94.5126 21.8163C96.1009 22.0392 97.8005 22.1785 99.6396 22.1785C101.479 22.1785 103.178 22.0392 104.766 21.8163C104.989 20.2281 105.129 18.5284 105.129 16.6894C105.129 14.8503 104.989 13.1507 104.766 11.5624Z" fill="#020000"/>
    <path d="M94.7354 23.293C95.7106 28.5035 97.7446 31.8193 99.6394 31.8193C101.534 31.8193 103.568 28.5035 104.543 23.293C103.011 23.488 101.367 23.5995 99.6394 23.5995C97.9118 23.5995 96.2679 23.488 94.7354 23.293Z" fill="#020000"/>
    <path d="M104.543 10.0859C103.568 4.87536 101.534 1.55957 99.6394 1.55957C97.7446 1.55957 95.7106 4.87536 94.7354 10.0859C96.2679 9.89084 97.9118 9.77938 99.6394 9.77938C101.367 9.77938 103.011 9.89084 104.543 10.0859Z" fill="#020000"/>
    <path d="M106.02 10.3087C110.423 11.0611 113.794 12.5657 115.299 14.4604C114.379 7.27159 108.667 1.58738 101.479 0.751465C103.568 2.06106 105.24 5.59976 106.048 10.3087H106.02Z" fill="#020000"/>
    <path d="M106.02 23.07C105.323 27.1939 103.958 30.4261 102.23 32.0422C108.89 30.9276 114.128 25.6892 115.215 19.0298C113.682 20.8688 110.339 22.3456 106.02 23.07Z" fill="#020000"/>
    <path d="M93.2587 10.3088C94.0667 5.59979 95.7386 2.03323 97.8562 0.723633C90.5838 1.53168 84.7881 7.27162 83.9243 14.5441C85.4011 12.5936 88.8005 11.0611 93.2587 10.3088Z" fill="#020000"/>
    <path d="M93.2586 23.0701C88.884 22.3178 85.5125 20.8132 84.0078 18.9463C85.0666 25.6615 90.3608 30.9834 97.0759 32.0701C95.3484 30.454 93.9552 27.2218 93.2586 23.098V23.0701Z" fill="#020000"/>
    <path d="M93.0634 11.7852C87.8529 12.7604 84.5371 14.7944 84.5371 16.6892C84.5371 18.5839 87.8529 20.618 93.0634 21.5932C92.8684 20.0607 92.7569 18.4167 92.7569 16.6892C92.7569 14.9616 92.8684 13.3177 93.0634 11.7852Z" fill="#020000"/>
    <path d="M74.8404 16.5779C74.8404 7.46645 67.4286 0.0546875 58.3172 0.0546875C49.2057 0.0546875 41.7939 7.46645 41.7939 16.5779C41.7939 25.6894 49.2057 33.1011 58.3172 33.1011C67.4286 33.1011 74.8404 25.6894 74.8404 16.5779ZM58.3172 31.7079C49.9859 31.7079 43.1871 24.937 43.1871 16.5779C43.1871 8.21877 49.9859 1.44788 58.3172 1.44788C66.6484 1.44788 73.4472 8.21877 73.4472 16.5779C73.4472 24.937 66.6763 31.7079 58.3172 31.7079Z" fill="#020000"/>
    <path d="M59.014 14.878V8.38574H57.6208V14.878L58.3174 15.5746L59.014 14.878Z" fill="#020000"/>
    <path d="M57.6208 18.2495V24.7696H59.014V18.2495L58.3174 17.5529L57.6208 18.2495Z" fill="#020000"/>
    <path d="M59.3205 16.5777L60.0171 17.2743H66.5094V15.8811H60.0171L59.3205 16.5777Z" fill="#020000"/>
    <path d="M56.6456 15.8811H50.1255V17.2743H56.6456L57.3422 16.5777L56.6456 15.8811Z" fill="#020000"/>
    <path d="M60.0171 15.8811L64.6147 11.2836L63.6116 10.2805L59.014 14.878L59.014 15.8811H60.0171Z" fill="#020000"/>
    <path d="M56.6456 17.2743L52.0481 21.8718L53.0233 22.847L57.6208 18.2495L57.6208 17.2743L56.6456 17.2743Z" fill="#020000"/>
    <path d="M58.0388 17.2743H57.6208L57.6208 18.2495L58.3174 17.5529L58.0388 17.2743Z" fill="#020000"/>
    <path d="M58.6239 15.8811L59.014 15.8811L59.014 14.878L58.3174 15.5746L58.6239 15.8811Z" fill="#020000"/>
    <path d="M57.6209 16.8563L57.3422 16.5777L56.6456 17.2743L57.6208 17.2743L57.6209 16.8563Z" fill="#020000"/>
    <path d="M59.014 16.2712L59.3205 16.5777L60.0171 15.8811H59.014V16.2712Z" fill="#020000"/>
    <path d="M59.014 15.8811L58.6239 15.8811L59.014 16.2712V15.8811Z" fill="#020000"/>
    <path d="M57.6208 17.2743H58.0388L57.6209 16.8563L57.6208 17.2743Z" fill="#020000"/>
    <path d="M59.014 18.2495L63.6116 22.847L64.6147 21.8718L60.0171 17.2743L59.014 17.2743L59.014 18.2495Z" fill="#020000"/>
    <path d="M57.6208 14.878L53.0233 10.2805L52.0481 11.2836L56.6456 15.8811H57.6208L57.6208 14.878Z" fill="#020000"/>
    <path d="M58.0388 15.8811L58.3174 15.5746L57.6208 14.878L57.6208 15.8811L58.0388 15.8811Z" fill="#020000"/>
    <path d="M58.6239 17.2743L58.3174 17.5529L59.014 18.2495L59.014 17.2743H58.6239Z" fill="#020000"/>
    <path d="M59.014 16.8563V17.2743L60.0171 17.2743L59.3205 16.5777L59.014 16.8563Z" fill="#020000"/>
    <path d="M57.6209 16.2712L57.6208 15.8811H56.6456L57.3422 16.5777L57.6209 16.2712Z" fill="#020000"/>
    <path d="M59.014 17.2743V16.8563L58.6239 17.2743H59.014Z" fill="#020000"/>
    <path d="M57.6208 15.8811L57.6209 16.2712L58.0388 15.8811L57.6208 15.8811Z" fill="#020000"/>
    <path d="M58.3174 17.5529L58.6239 17.2743H58.0388L58.3174 17.5529Z" fill="#020000"/>
    <path d="M58.3174 15.5746L58.0388 15.8811H58.6239L58.3174 15.5746Z" fill="#020000"/>
    <path d="M59.014 16.8563L59.3205 16.5777L59.014 16.2712V16.8563Z" fill="#020000"/>
    <path d="M57.3422 16.5777L57.6209 16.8563V16.2712L57.3422 16.5777Z" fill="#020000"/>
    <path d="M58.6239 15.8811H58.0388L57.6209 16.2712V16.8563L58.0388 17.2743H58.6239L59.014 16.8563V16.2712L58.6239 15.8811Z" fill="#020000"/>
  </g>
  <defs>
    <clipPath id="clip0_2363_1613">
      <rect width="135.502" height="72" fill="white" transform="translate(0.5 0.0546875)"/>
    </clipPath>
  </defs>
</svg>`
  }
]

const Service = ({ service, index }) => {
  return (
    <div className="service border border-solid border-electricViolet rounded-20 pt-14 pb-16 px-10">
      <span className="text-base">0{index + 1}</span>
      <div className="my-10" dangerouslySetInnerHTML={{ __html: service.icon }}>
        {/* <img alt="" className="my-10" src={service.icon} /> */}
      </div>
      <h3 className="text-2.5xl mb-10">{service.title}</h3>
      <p className="text-tiny leading-loose tracking-wider">
        {service.description}
      </p>
    </div>
  )
}

const CaseStudies = ({
  data: {
    contentfulCaseStudies: { title, subtitle, caseStudies, testimonials, seo }
  },
  location
}: Props) => {
  gsap.registerPlugin(ScrollTrigger)
  const asterikRef = useRef(null)

  useEffect(() => {
    const asterik = asterikRef.current
    const caseStudyArticles = [
      ...document.querySelectorAll('.case-study-listing')
    ]

    const rotate = gsap
      .timeline({
        scrollTrigger: {
          trigger: asterik,
          scrub: 0,
          start: 'top 80%',
          end: '+=10000'
        }
      })
      .to(asterik, {
        rotation: 360 * 5,
        duration: 1,
        ease: 'none'
      })

    caseStudyArticles.forEach((section, index) => {
      const img = section.querySelector('.img')
      const info = section.querySelector('.info')
      const layout = (index + 1) % 2 === 0 ? 'right' : 'left'

      gsap.set(img, { autoAlpha: 0, y: 120, x: layout === 'left' ? -120 : 120 })
      gsap.set(info, {
        autoAlpha: 0,
        y: 120,
        x: layout === 'left' ? 120 : -120
      })

      gsap.to([img, info], {
        autoAlpha: 1,
        y: 0,
        x: 0,
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          scrub: true,
          end: '+=500'
          // markers: true
        }
      })
    })
  }, [])

  return (
    <>
      <Meta
        bodyAttributes={{ class: 'new-nav-padding' }}
        description={seo.seoDescription?.seoDescription}
        location={location}
        shareImage={seo.seoShareImage?.file?.url}
        title={seo.seoTitle}
      />
      <main className="CaseStudies overflow-hidden">
        <section className="px-6 py-28 case-studies-hero text-newBlack md:px-12 md:py-12 lg:py-0">
          <div className="w-full max-w-3xl m-auto">
            <h1
              className="font-primary text-7xl leading-tight font-extralight text-center md:px-10"
              dangerouslySetInnerHTML={{ __html: title.title }}
            />
            <p className="text-base font-light text-center leading-relaxed mt-10">
              {subtitle.subtitle}
            </p>
            <div className="grid w-full place-items-center mt-10">
              <div ref={asterikRef} className="">
                <Asterisk />
              </div>
            </div>
          </div>
        </section>

        <section className="CaseStudies-items mt-20 2xl:mt-32">
          {caseStudies.map((caseStudy, idx: number) => {
            const layout = (idx + 1) % 2 === 0 ? 'order-3' : 'order-0'
            return (
              <>
                <CaseStudy
                  key={caseStudy.slug}
                  caseStudy={caseStudy}
                  layout={layout}
                />
              </>
            )
          })}
        </section>

        <section className="our-clients bg-newBlack py-20 lg:py-28">
          <div className="our-clients__wrapper px-6 max-w-6xl mx-auto md:flex justify-start items-center xl:px-0">
            <h2 className="text-h2 text-white md:mr-14">Our clients love us</h2>
            <p className="font-light text-body text-white mt-8 max-w-md md:mt-0">
              Our expertise in and obsession with strategy, design, and
              optimization make us a triple threat.
            </p>
          </div>
          <TestimonialSlider
            buttonClasses="lg:hover:bg-gray-400 lg:hover:text-gray-800 transition-all duration-300 ease-in-out"
            color="white"
            logoClasses="grayscale brightness-400 border-white"
            testimonials={testimonials}
          />
        </section>

        <section>
          <div className="our-clients__wrapper px-6 pt-42 max-w-6xl mx-auto lg:flex justify-start items-center lg:items-start xl:px-0">
            <h2 className="text-h2 font-light md:mr-14 w-2/3 lg:max-w-xxs">
              A Tried & True Approach
            </h2>
            <p className="font-light text-body mt-8 lg:max-w-sm xl:max-w-md lg:mt-0">
              Our powerful three-step process allows us to get to market
              efficiently, allowing us to iterate & optimize quickly to deliver
              optimal results.
            </p>
            <AniLink
              bg="#F3F3F3"
              className="flex items-center text-blueRibbon font-light mt-12 group lg:ml-auto lg:my-auto"
              cover
              direction="right"
              duration={1.5}
              to="/what-we-do/"
            >
              <span>Our services</span>
              <svg
                className="w-8 h-auto ml-13 lg:group-hover:translate-x-3"
                fill="none"
                viewBox="0 0 32 32"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  clipRule="evenodd"
                  d="M24.2753 7.33331C24.2753 11.6662 27.7337 15.1787 32 15.1787V15.4583V15.7379V16.2621V16.5416V16.8212C27.7338 16.8212 24.2753 20.3337 24.2753 24.6666H23.2086C23.2086 21.0582 25.3163 17.9497 28.3495 16.5416L0 16.5416V15.4583L28.3495 15.4583C25.3163 14.0503 23.2086 10.9418 23.2086 7.33331H24.2753Z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
            </AniLink>
          </div>
          <div className="px-6 max-w-6xl mx-auto mt-17 mb-42 lg:grid lg:grid-cols-3">
            {services.map((service, index) => (
              <Service key={service.title} index={index} service={service} />
            ))}
          </div>
        </section>

        {/* <div className="CaseStudies-process">
          <h3 className="text-h3 font-extrabold mb-4">
            A Tried & True Approach
          </h3>
          <p className="text-body mb-12 lg:mb-14 xl:mb-16">
            Our powerful three-step process allows us to get to market
            efficiently, allowing us to iterate & optimize quickly to deliver
            optimal results.
          </p>
          <ProcessSteps />

          <div className="CaseStudies-process-button">
            <MotionAniLink
              className="inline-block"
              styleType="solid-purple"
              to="/what-we-do/"
            >
              Discover What We Do
            </MotionAniLink>
          </div>
        </div> */}

        {/* <div className="CaseStudies-opportunity-form">
          <OpportunityForm
            buttonText="Let's Meet"
            location={location.href}
            title="Set up a meeting — we’d love to chat"
          />
        </div> */}
      </main>
      <NewFooter />
    </>
  )
}

export const CASE_STUDIES_PAGE_QUERY = graphql`
  query caseStudiesPageQuery {
    contentfulCaseStudies(pageTitle: { eq: "Case Studies" }) {
      title {
        title
      }
      subtitle {
        subtitle
      }
      caseStudies {
        name
        slug
        tagline
        successSummary {
          successSummary
        }
        service {
          shortTitle
          serviceGif {
            file {
              url
            }
          }
        }
        badge {
          file {
            url
          }
        }
        stats {
          type
          stage
          amountRaised
          founded
        }
        listingImage {
          fluid(maxWidth: 1200) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
      testimonials {
        company
        quoteShort {
          raw
        }
        name
        role
        bwHeadshot {
          fluid {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        companyLogo {
          file {
            url
            fileName
          }
        }
        caseStudy {
          slug
        }
      }

      seo {
        seoTitle
        seoDescription {
          seoDescription
        }
        seoShareImage {
          file {
            url
          }
        }
      }
    }
  }
`

export default CaseStudies
